import React, { useState } from 'react';
import emailjs from 'emailjs-com';

const HEAR_ABOUT_OPTIONS = [
  'LinkedIn',
  'Facebook',
  'Twitter',
  'Instagram',
  'TikTok',
  'Referred by someone',
  'Other'
];

const EMPTY_FORM = {
  name: '',
  phone: '',
  email: '',
  subject: '',
  message: '',
  hearAbout: '',
  hearAboutOther: ''
};

const Contact = () => {
  const [formData, setFormData] = useState(EMPTY_FORM);

  const [showNotification, setShowNotification] = useState(false);
  const [showSendingNotification, setShowSendingNotification] = useState(false);

  // Send Message is only active when name, phone and "where did you hear about us" are filled.
  // If "Other" is chosen, the specify box must also be filled.
  const isFormValid =
    formData.name.trim() !== '' &&
    formData.phone.trim() !== '' &&
    formData.hearAbout !== '' &&
    (formData.hearAbout !== 'Other' || formData.hearAboutOther.trim() !== '');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      // Clear the "Other" text if they switch to a different option
      ...(name === 'hearAbout' && value !== 'Other' ? { hearAboutOther: '' } : {})
    }));
  };

  const handleSubmit = async () => {
    if (!isFormValid) return;

    // Show sending notification and clear fields immediately
    setShowSendingNotification(true);
    const formDataToSend = { ...formData }; // Store current form data
    setFormData(EMPTY_FORM);

    const heardFrom =
      formDataToSend.hearAbout === 'Other'
        ? `Other: ${formDataToSend.hearAboutOther.trim()}`
        : formDataToSend.hearAbout;

    try {
      await emailjs.send(
        'service_sjb8ihu',
        'template_ttvzb7c',
        {
          from_name: formDataToSend.name,
          from_phone: formDataToSend.phone,
          from_email: formDataToSend.email,
          subject: formDataToSend.subject,
          message: formDataToSend.message,
          hear_about: heardFrom,
          time: new Date().toLocaleString('en-KE', { dateStyle: 'medium', timeStyle: 'short' }),
          to_email: 'support@infinitytechafrica.com'
        },
        'zADH0lwzRrZSev2vT'
      );
      
      setShowSendingNotification(false);
      setShowNotification(true);
      setTimeout(() => setShowNotification(false), 5000);
    } catch (error) {
      console.error('Error:', error);
      setShowSendingNotification(false);
    }
  };

  const handlePhoneClick = () => {
    window.location.href = 'tel:+254722943271';
  };

  const handleEmailClick = () => {
    window.location.href = 'mailto:support@infinitytechafrica.com';
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      {/* Sending Notification */}
      {showSendingNotification && (
        <div className="fixed top-4 right-4 bg-blue-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
          Sending message...
        </div>
      )}

      {/* Success Notification */}
      {showNotification && (
        <div className="fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center">
          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
          </svg>
          Message Successfully Sent
          <button 
            onClick={() => setShowNotification(false)}
            className="ml-4 text-white hover:text-gray-200"
          >
            ×
          </button>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-blue-900 mb-4">Get In Touch</h1>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Contact Information Card */}
          <div className="bg-blue-900 rounded-3xl p-8 text-white">
            <h2 className="text-2xl font-bold mb-8">Contact Information</h2>
            
            {/* Phone */}
            <div className="flex items-center mb-6">
              <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center mr-4">
                <svg className="w-4 h-4 text-blue-900" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/>
                </svg>
              </div>
              <span 
                className="text-lg cursor-pointer hover:text-blue-200 transition-colors duration-200"
                onClick={handlePhoneClick}
              >
                +2547 2294 3271
              </span>
            </div>

            {/* Email */}
            <div className="flex items-center mb-12">
              <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center mr-4">
                <svg className="w-4 h-4 text-blue-900" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
                </svg>
              </div>
              <span 
                className="text-lg cursor-pointer hover:text-blue-200 transition-colors duration-200"
                onClick={handleEmailClick}
              >
                info@infinitytechafrica.com
              </span>
            </div>

            {/* Map */}
            <div className="relative">
              <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center mb-4 mx-auto">
                <svg className="w-5 h-5 text-blue-900" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"/>
                </svg>
              </div>
              
              <div className="bg-white rounded-2xl p-4 shadow-lg">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3988.7733139419244!2d36.78673701475445!3d-1.2621440990618574!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x182f1a6a7fab31a1%3A0x52b3b96c15a2c0e5!2sMpaka%20Rd%2C%20Nairobi!5e0!3m2!1sen!2ske!4v1703097600000!5m2!1sen!2ske"
                  width="100%"
                  height="192"
                  style={{ border: 0, borderRadius: '12px' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Company Location - Westlands, Mpaka Road"
                ></iframe>
                <div className="mt-3 text-center">
                  <p className="text-blue-900 font-semibold">Nairobi Westlands, Mpaka Road</p>
                  <p className="text-blue-700 text-sm">Mayfair Maisonettes</p>
                  <p className="text-blue-700 text-sm">Opposite Fedha Plaza</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="space-y-6">
            <div className="space-y-6">
              {/* Name and Phone Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name *"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-0 py-3 border-0 border-b-2 border-gray-300 bg-transparent focus:border-blue-600 focus:outline-none focus:ring-0 placeholder-gray-500 text-gray-900"
                  />
                </div>
                <div>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Your Phone Number *"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-0 py-3 border-0 border-b-2 border-gray-300 bg-transparent focus:border-blue-600 focus:outline-none focus:ring-0 placeholder-gray-500 text-gray-900"
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <input
                  type="email"
                  name="email"
                  placeholder="Your Email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-0 py-3 border-0 border-b-2 border-gray-300 bg-transparent focus:border-blue-600 focus:outline-none focus:ring-0 placeholder-gray-500 text-gray-900"
                />
              </div>

              {/* Subject */}
              <div>
                <input
                  type="text"
                  name="subject"
                  placeholder="Your Subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  className="w-full px-0 py-3 border-0 border-b-2 border-gray-300 bg-transparent focus:border-blue-600 focus:outline-none focus:ring-0 placeholder-gray-500 text-gray-900"
                />
              </div>

              {/* Message */}
              <div>
                <textarea
                  name="message"
                  placeholder="Write Here Your Message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows="6"
                  className="w-full px-0 py-3 border-0 border-b-2 border-gray-300 bg-transparent focus:border-blue-600 focus:outline-none focus:ring-0 placeholder-gray-500 text-gray-900 resize-none"
                ></textarea>
              </div>

              {/* Where did you hear about us? */}
              <div>
                <select
                  name="hearAbout"
                  value={formData.hearAbout}
                  onChange={handleInputChange}
                  required
                  className={`w-full px-0 py-3 border-0 border-b-2 border-gray-300 bg-transparent focus:border-blue-600 focus:outline-none focus:ring-0 ${
                    formData.hearAbout === '' ? 'text-gray-500' : 'text-gray-900'
                  }`}
                >
                  <option value="" disabled>Where did you hear about us? *</option>
                  {HEAR_ABOUT_OPTIONS.map(option => (
                    <option key={option} value={option} className="text-gray-900">
                      {option}
                    </option>
                  ))}
                </select>

                {formData.hearAbout === 'Other' && (
                  <input
                    type="text"
                    name="hearAboutOther"
                    placeholder="Kindly specify where..."
                    value={formData.hearAboutOther}
                    onChange={handleInputChange}
                    className="w-full mt-4 px-3 py-2 border border-gray-300 rounded-md bg-transparent focus:border-blue-600 focus:outline-none focus:ring-0 placeholder-gray-500 text-gray-900"
                  />
                )}
              </div>

              {/* Submit Button */}
              <button
                onClick={handleSubmit}
                disabled={!isFormValid}
                className={`font-semibold px-8 py-3 rounded-lg transition-colors duration-200 text-white ${
                  isFormValid
                    ? 'bg-blue-900 hover:bg-blue-800 cursor-pointer'
                    : 'bg-gray-400 cursor-not-allowed'
                }`}
              >
                Send Message
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Footer Section */}
      <footer className="bg-blue-600 text-white py-12">
          <div className="max-w-7xl mx-auto px-6">
            
            {/* Top 3 Columns */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10 border-b border-blue-500 pb-10">
              
              {/* About Us */}
              <div className="space-y-3 md:border-r border-white md:pr-6">
                <h3 className="text-lg font-bold mb-2">About Us</h3>
                <ul className="text-sm space-y-2">
                  <li><a href="about" className="hover:text-blue-200">Who we are</a></li>
                  <li><a href="about" className="hover:text-blue-200">News Hub</a></li>
                  <li><a href="about" className="hover:text-blue-200">Our Reach</a></li>
                  
                </ul>
              </div>

              {/* Customer Service */}
              <div className="space-y-3 md:border-r border-white md:px-6">
                <h3 className="text-lg font-bold mb-2">Customer Service</h3>
                <ul className="text-sm space-y-2">
                  <li><a href="contact" className="hover:text-blue-200">Message Us</a></li>
                  
                  <li><a href="contact" className="hover:text-blue-200">Comment/Complaint</a></li>
                </ul>
              </div>

              {/* Join + Payment */}
              <div className="space-y-4 md:pl-6">
                
                <div>
                <h4 className="text-sm font-semibold mb-2">Payment Options</h4>
                <div className="flex space-x-4">
                  <img src="/icons/mpesa2.png" alt="Visa" className="h-12 object-contain" />
                  <img src="/icons/mastercard.png" alt="Mastercard" className="h-6 object-contain" />
                  <img src="/icons/visa.png" alt="Amex" className="h-6 object-contain" />
                </div>
              </div>
              </div>
            </div>

            {/* Social Media Section - New Row */}
            <div className="flex justify-center mt-10">
              <div className="flex space-x-4 border border-white p-3 rounded">
                <a href="https://www.facebook.com/SMSAfrica01?mibextid=wwXIfr&rdid=M64RTVM7l0SsUXbw&share_url=https%3A%2F%2Fwww.facebook.com%2Fshare%2F1D45K1gBPv%2F%3Fmibextid%3DwwXIfr#" target="_blank" rel="noopener noreferrer"><img src="/icons/facebook.png" alt="Facebook" className="w-6 h-6" /></a>
                <a href="https://www.linkedin.com/company/infinity-tech-africa/" target="_blank" rel="noopener noreferrer"><img src="/icons/linkedin.png" alt="LinkedIn" className="w-6 h-6" /></a>
                <a href="https://www.instagram.com/smsafrica/profilecard/?igsh=eXgwOHBydWdlZGVi" target="_blank" rel="noopener noreferrer"><img src="/icons/email.png" alt="Email" className="w-6 h-6" /></a>
                <a href="https://www.twitter.com" target="_blank" rel="noopener noreferrer"><img src="/icons/x logo.png" alt="Twitter" className="w-6 h-6" /></a>
                <a href="https://www.tiktok.com/@infinitytechafrica?_t=ZM-8xr2bhp0ey0&_r=1" target="_blank" rel="noopener noreferrer"><img src="/icons/tiktok logo2.png" alt="YouTube" className="w-6 h-6" /></a>
              </div>
            </div>

            {/* Bottom Footer */}
            <div className="text-center pt-6 mt-6 border-t border-blue-500 text-sm">
              <p>&copy; 2025 Infinity Tech Africa | Privacy Policy | Terms & Conditions Apply</p>
            </div>
          </div>
        </footer>

    </div>
  );
};

export default Contact;